import {
	FileUploadUrlApiRequestBody,
	FileUploadUrlApiResponseBody,
	HttpMethod,
	RedirectionStatus,
	SuccessStatus,
	isErr,
	isOk,
} from "@jace/api";
import { EventEmitter, Unsubscribe, suuid, unwrapOr } from "@zetalabs/core";
import { calcProgress, isDone, isRunning } from "../common";
import { ComputedValue } from "../common/computed-value";
import { getFileMetadata, parseUnifiedUri } from "../common/parse-unified-uri";
import { SubjectHash, parseSubjectId } from "../hash";
import { FileManager } from "../manager";
import { Threads } from "../scheduler";
import { SessionUtils } from "../session-utils";
import {
	EventPayload,
	FileManagerConfiguration,
	FileUpload,
	ProcessStatus,
	ProcessingStatus,
	Session,
	SessionEvent,
	SessionManager,
	SessionType,
	TerminationStatus,
} from "../types";
import { InitialUploadData } from "./types";

class UploadError extends Error {}

const PREVIEW_IMAGE_MIME_TYPES = [
	"image/jpeg",
	"image/jpg",
	"image/png",
	"image/gif",
	"image/webp",
	"image/svg+xml",
	"image/avif",
	"image/bmp",
];

export function getThumbnailUrl(file: File): string | null {
	if (PREVIEW_IMAGE_MIME_TYPES.includes(file.type)) {
		return URL.createObjectURL(file);
	}

	return null;
}

type FileUploadConfiguration = FileManagerConfiguration["uploads"] & {
	initialData: InitialUploadData | null;
} & Record<string, any>;

const defaultFileUploadConfig: FileUploadConfiguration = {
	concurrent: 1,
	maxCount: 10,
	maxSize: 10 * 1024 * 1024, // 10MB
	multiple: true,
	initialData: null,
};

// Track external data to detect deletion
export class UploadSessionManager implements SessionManager {
	public readonly config: FileUploadConfiguration;

	private _items = new Map<string, FileUpload>();
	private _deleted = new Set<string>();
	private _queued = new Set<string>();

	private _request: ReturnType<typeof setTimeout> | null = null;
	private _controllers = new Map<string, AbortController>();

	private _em = new EventEmitter<SessionEvent>();

	private _state: ComputedValue<Session<FileUpload, FileUploadConfiguration>>;
	private _errors = new Set<Error>();

	// TODO: have a more granular status on upload session
	//private status: UploadSessionStatus = "idle";

	constructor(
		public readonly subject: SubjectHash,
		public readonly manager: FileManager,
		config: Partial<FileUploadConfiguration> = {},
	) {
		this.subject = subject;
		this.manager = manager;

		const { initialData, ...c } = config;

		this.config = Object.assign(
			{},
			defaultFileUploadConfig,
			this.manager.config,
			c,
		);

		if (initialData && this._items.size === 0) {
			this.updateData(initialData, this._items);
		}

		this._state = new ComputedValue(() => {
			const mergedItems = this.toArray().filter(
				(item) => !this._deleted.has(item.id),
			);
			const progress = calcProgress(mergedItems);
			const errors = Array.from(this._errors);

			let status: ProcessStatus = TerminationStatus.Idle;

			if (isDone(mergedItems) && this._items.size > 0) {
				status = TerminationStatus.Done;
			}

			if (isRunning(mergedItems)) {
				status = ProcessingStatus.InProgress;
			}

			const now = Date.now();

			return {
				subject: this.subject,
				items: mergedItems,
				status,
				progress,
				errors,
				config: this.config,
				updatedAt: now,
			};
		});
	}

	public get session(): Session<FileUpload, FileUploadConfiguration> {
		return this._state.compute();
	}

	public get size(): number {
		return this._items.size;
	}

	public init(data: InitialUploadData): [added: string[], removed: string[]] {
		return this.updateData(data, this._items);
	}

	private updateData = (
		data: InitialUploadData,
		target: Map<string, FileUpload>,
	): [added: string[], removed: string[]] => {
		if (!Array.isArray(data.uris)) {
			return [[], []];
		}

		const items = this.toArray();

		const markedForDeletion = items.reduce<string[]>((acc, item) => {
			if (!data.uris.includes(item.unifiedUri ?? item.id)) {
				acc.push(item.id);
			}

			return acc;
		}, []);

		const added: string[] = [];

		data.uris.forEach((uri) => {
			if (target.has(uri)) {
				return;
			}

			added.push(uri);

			const result = parseUnifiedUri(uri);

			if (isErr(result)) {
				console.error(result.error);
				return;
			}

			const { name, ext } = result.value;

			target.set(uri, {
				id: uri,
				name,
				ext,
				type: SessionType.Upload,
				sessionId: this.subject.value,
				progress: 1,
				error: null,
				source: null,
				thumbnailSrc: undefined,
				status: TerminationStatus.Done,
				unifiedUri: uri,
			});
		});

		markedForDeletion.forEach((uri) => {
			this.delete(uri, true);
		});

		return [added, markedForDeletion];
	};

	public async dispose(): Promise<void> {
		this._controllers.forEach((controller) => {
			controller.abort();
		});

		this.manager.deleteSession(this.session.subject);
	}

	public upload = (file: File): FileUpload | undefined => {
		this._errors.clear();

		try {
			if (this.size + 1 > this.manager.config.uploads.maxCount) {
				throw new UploadError(
					`Max uploads count reached at file ${file.name}. Please try again with fewer files.`,
				);
			}

			if (
				Array.from(this._items.values()).reduce<number>((acc, item) => {
					const size = item.source ? item.source.size : 0;
					return acc + size;
				}, 0) +
					file.size >
				this.manager.config.uploads.maxSize
			) {
				throw new UploadError(
					`Max uploads size reached with file ${file.name}. Please try again with a smaller file.`,
				);
			}

			const tmpId = suuid();
			const { name, ext } = getFileMetadata(file);
			const thumbnailSrc = getThumbnailUrl(file);

			const upload = {
				id: tmpId,
				type: SessionType.Upload,
				name,
				ext,
				unifiedUri: null,
				source: file,
				status: TerminationStatus.Idle,
				sessionId: this.subject.value,
				progress: 0,
				error: null,
				thumbnailSrc,
			} satisfies FileUpload;

			this._process(upload);

			return upload;
		} catch (e) {
			const error = unwrapOr(e, new UploadError(JSON.stringify(e)));
			this._errors.add(error);

			// FIXME: This should be on error though
			this._em.emit("change", this.session);
		}
	};

	public on<E extends SessionEvent>(
		event: E,
		callback: (data: EventPayload<E, FileUpload>) => void,
	): Unsubscribe {
		if (typeof callback !== "function") {
			throw new Error("Callback must be a function");
		}

		return this._em.on(event, callback);
	}

	public reset(): void {
		this._items.clear();
		this._queued.clear();

		this._em.emit("change", this.session);
	}

	// NOTE: (bwilytsch): Deletes a file from the session,
	// but does not delete it from the server
	public delete = async (id: string, soft?: boolean): Promise<void> => {
		const item = this._items.get(id);

		if (item) {
			this._deleted.add(id);

			item.status = TerminationStatus.Cancelled;
			const controller = this._controllers.get(id);

			if (controller) {
				controller.abort();
			}

			this._items.delete(id);
			this._em.emit("change", this.session);

			if (!Boolean(soft)) {
				const result = await this.manager.transport.delete("/files", {
					data: {
						unified_uri_str: item.unifiedUri,
					},
				});

				if (isErr(result)) {
					// rollback
				}
			}

			return;
		}
	};

	public getUris = async (): Promise<string[]> => {
		return new Promise((resolve) => {
			const session = this._state.compute();

			if (session.status === TerminationStatus.Done) {
				resolve(SessionUtils.from(session));
				return;
			}

			const poll = async (
				data: Session<FileUpload, FileUploadConfiguration>,
			) => {
				resolve(SessionUtils.from(data));

				this._em.off("change", poll);
				this._em.off("done", poll);
			};
		});
	};

	private _process = async (item: FileUpload): Promise<void> => {
		if (this._queued.has(item.id)) {
			return;
		}

		item.status = ProcessingStatus.Preparing;

		this._queued.add(item.id);
		this._items.set(item.id, item);

		if (this._request) {
			clearTimeout(this._request);
			this._request = null;
		}

		// Debounce requests so we can do concurrent requests
		this._request = setTimeout(async () => {
			this._em.emit("change", this.session);
			const candidates = Array.from(this._queued);
			this._queued.clear();

			await this._requestObjectStorageData(candidates);

			this._request = null;
		}, 50);
	};

	private async _requestObjectStorageData(chunks: string[]): Promise<void> {
		// Add concurrency here, would I need to convert this into
		// a iterator for easy parallelization?
		await Promise.all(
			chunks.map(async (id) => {
				const item = this._items.get(id);

				if (item && item.source) {
					if (item.status === TerminationStatus.Cancelled) {
						return;
					}

					const objectStorageDataResult = await Threads.spawn(() => {
						if (!item.source) {
							throw new Error("No source");
						}

						return this.manager.transport.post<FileUploadUrlApiResponseBody>(
							"/files/upload-url",
							{
								data: {
									file_name: item.source.name,
									mime_type: item.source.type,
									thread_id: parseSubjectId(this.session.subject),
								} satisfies FileUploadUrlApiRequestBody,
							},
						);
					});

					if (isOk(objectStorageDataResult)) {
						const { unified_uri, url: uploadUrl } =
							objectStorageDataResult.value;
						item.unifiedUri = unified_uri;
						item.status = ProcessingStatus.InProgress;
						const tempId = item.id;

						item.id = unified_uri;

						// update record
						this._items.delete(tempId);
						this._items.set(unified_uri, item);

						this._em.emit("change", this.session);

						Threads.spawn(async () => this._uploadFile(uploadUrl, item));
					}
				}
			}),
		);
	}

	private async _uploadFile(target: string, item: FileUpload): Promise<void> {
		if (!item.source) {
			return;
		}

		const abortController = new AbortController();
		this._controllers.set(item.id, abortController);
		const signal = abortController.signal;

		const xhr = new XMLHttpRequest();
		xhr.open(HttpMethod.Put, target);

		signal.addEventListener("abort", () => {
			xhr.abort();
		});

		// Progress
		xhr.upload.onprogress = (event) => {
			item.progress = event.loaded / event.total;
			this._em.emit("change", this.session);
		};

		// Start
		xhr.upload.onloadstart = (_event) => {
			item.progress = 0;
			this._em.emit("change", this.session);
		};

		// Done
		xhr.upload.onload = (_event) => {
			// Start cleanup before the rest
			this._controllers.delete(item.id);

			item.progress = 1;
			item.status = TerminationStatus.Ready;

			this._em.emit("change", this.session);
		};

		xhr.onload = () => {
			if (
				xhr.status >= SuccessStatus.Ok &&
				xhr.status < RedirectionStatus.MultipleChoices
			) {
				item.status = TerminationStatus.Done;
				this._em.emit("change", this.session);
			}

			if (isDone(this.toArray())) {
				this._em.emit("done", this.session);
			}
		};

		// Error
		xhr.upload.onerror = () => {
			item.status = TerminationStatus.Error;
			item.error = "Upload failed";

			this._em.emit("change", this.session);
		};

		xhr.setRequestHeader("Content-Type", item.source.type);
		xhr.send(item.source);
	}

	private toArray = (): FileUpload[] => {
		return Array.from(this._items.values());
	};
}
